import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FiX } from "react-icons/fi";
import { MAIN_NAV } from "../../config/navigation";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  background-color: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease;
`;

const Panel = styled.div`
  position: fixed;
  inset: 0 0 0 auto;
  z-index: ${({ theme }) => theme.zIndex.drawer};
  display: flex;
  width: min(320px, 85vw);
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.menu};
  transform: translateX(${({ $open }) => ($open ? "0" : "100%")});
  transition: transform 0.25s ease;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Heading = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const CloseButton = styled.button`
  display: inline-flex;
  padding: 8px;
  border: none;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceAlt};
  }
`;

const Nav = styled.nav`
  overflow-y: auto;
  padding: 8px 0 24px;
`;

const ItemList = styled.ul`
  list-style: none;
`;

const SubList = styled.ul`
  list-style: none;
  padding-left: 16px;
`;

const ItemLink = styled(NavLink)`
  display: block;
  padding: 12px 20px;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.md};

  &:hover,
  &.active {
    background-color: ${({ theme }) => theme.colors.primarySoft};
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: none;
  }
`;

const SubLink = styled(ItemLink)`
  padding: 10px 20px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

/**
 * Мобильное меню без antd: минус ~600 КБ зависимостей ради одного Drawer.
 * Закрывается по Esc, клику по подложке и переходу на другую страницу;
 * фон под меню не скроллится.
 */
export function MobileMenu({ open, onClose, returnFocusRef }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    const { overflow } = document.body.style;
    // Элемент, которому нужно вернуть фокус, фиксируем на момент открытия.
    const triggerElement = returnFocusRef?.current;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", handleKeyDown);
      triggerElement?.focus();
    };
  }, [open, onClose, returnFocusRef]);

  return (
    <>
      <Overlay $open={open} onClick={onClose} aria-hidden="true" />
      <Panel
        ref={panelRef}
        $open={open}
        role="dialog"
        aria-modal="true"
        aria-label={t("menu")}
        aria-hidden={!open}
        tabIndex={-1}
        // Скрытая панель не должна ловить фокус табом.
        inert={!open}
      >
        <Head>
          <Heading>{t("menu")}</Heading>
          <CloseButton type="button" onClick={onClose} aria-label={t("close")}>
            <FiX size={22} />
          </CloseButton>
        </Head>

        <Nav>
          <ItemList>
            {MAIN_NAV.map((item) => (
              <li key={item.id}>
                <ItemLink to={item.to} end={item.end} onClick={onClose}>
                  {t(item.labelKey)}
                </ItemLink>

                {item.children && (
                  <SubList>
                    {item.children.map((child) => (
                      <li key={child.id}>
                        <SubLink to={child.to} onClick={onClose}>
                          {t(child.labelKey)}
                        </SubLink>
                      </li>
                    ))}
                  </SubList>
                )}
              </li>
            ))}
          </ItemList>
        </Nav>
      </Panel>
    </>
  );
}

export default MobileMenu;
